/**
 * Route guards — `RequireAuth`, `RequirePermission`, `RequireFeature`.
 *
 * ┌──────────────────────────────────────────────────────────────────────┐
 * │ **THIS IS PRESENTATION, NOT THE SECURITY BOUNDARY.**                 │
 * │                                                                      │
 * │ Every check here runs in the browser, on state the browser was       │
 * │ handed, in code the user can edit. Its entire purpose is to avoid    │
 * │ showing someone a screen that would only 403 — a courtesy, not a     │
 * │ control. The boundary is `requirePermission()` and `requireFeature()` │
 * │ in the API, which run on every request regardless of what the client │
 * │ believes.                                                            │
 * │                                                                      │
 * │ The failure this comment exists to prevent: a future screen that     │
 * │ renders an action *only* behind one of these guards and skips the    │
 * │ server-side key, on the reasoning that "the nav already hides it".   │
 * │ Criterion 12 asserts a hidden control is still 403 at the API.       │
 * └──────────────────────────────────────────────────────────────────────┘
 *
 * The two guards are kept separate for the same reason the API keeps them
 * separate (§4, §5): *may this person do it* and *has this account bought it*
 * are different questions with different answers and different fixes. Collapsed
 * into one they would produce "you do not have access" for a feature the
 * customer simply has not purchased, and the administrator would go looking in
 * the wrong place.
 */

import type { FeatureKey, PermissionKey } from '@rd/shared';
import type { ReactElement, ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';

import { ErrorState } from '../components/ErrorState.js';
import { Spinner } from '../components/Spinner.js';

import { useAuth } from './AuthProvider.js';

export function RequireAuth({ children }: { children: ReactNode }): ReactElement {
  const { status, mustChangePassword } = useAuth();
  const location = useLocation();

  if (status === 'loading') return <BootSpinner />;
  if (status === 'anonymous') {
    // `from` so the user lands where they were headed once signed in.
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  if (mustChangePassword && location.pathname !== '/password/change') {
    // §04.4 / E7: the shell stays blocked until the change succeeds.
    return <Navigate to="/password/change" replace />;
  }

  return <>{children}</>;
}

export function RequirePermission({
  permission,
  children,
}: {
  permission: PermissionKey | PermissionKey[];
  children: ReactNode;
}): ReactElement {
  const { hasPermission } = useAuth();
  const keys = Array.isArray(permission) ? permission : [permission];

  // Presentation only — see the header. The API refuses this independently.
  if (!hasPermission(...keys)) {
    return (
      <ErrorState
        title="You do not have access to this"
        body="Your administrator can grant it if you need it."
      />
    );
  }
  return <>{children}</>;
}

export function RequireFeature({
  feature,
  children,
}: {
  feature: FeatureKey | FeatureKey[];
  children: ReactNode;
}): ReactElement {
  const { hasFeature } = useAuth();
  const keys = Array.isArray(feature) ? feature : [feature];

  // Presentation only — see the header. The API refuses this independently.
  if (!hasFeature(...keys)) {
    return (
      <ErrorState
        title="Not available on this account"
        body="This feature is not enabled here. Contact your administrator to ask about it."
      />
    );
  }
  return <>{children}</>;
}

/** Platform-scope screens (Phase 07 builds on this). */
export function RequirePlatformScope({ children }: { children: ReactNode }): ReactElement {
  const { user } = useAuth();
  if (user?.scope !== 'PLATFORM') {
    return <ErrorState title="Not found" body="This may have been removed, or you may not have access to it." />;
  }
  return <>{children}</>;
}

function BootSpinner(): ReactElement {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <Spinner label="Loading your account" />
    </div>
  );
}
